export const DEFAULT_WORKSPACE_SIZES = [15, 85] as const;
export const DEFAULT_CENTER_RIGHT_SIZES = [64.7, 35.3] as const;
/** LeftPane の上下比率 (上=環境ファイル / 下=チャット)。 */
export const DEFAULT_LEFT_PANE_SIZES = [50, 50] as const;
export const DEFAULT_BOTTOM_PANE_HEIGHT = 30;

/** ワークスペース (中央+右) の下限 (%)。左ペインはこの残りより広くできない。 */
export const WORKSPACE_MIN_SIZE = 50;

export interface PersistedLayoutState {
	workspaceSizes: number[];
	/** workspaceSizes を保存したときの左右分割グループの実寸 (px)。未記録なら null。 */
	workspaceGroupWidth: number | null;
	centerRightSizes: number[];
	leftPaneSizes: number[];
	bottomPaneHeight: number;
	sidebarCollapsed: boolean;
	leftPaneCollapsed: boolean;
	environmentFilesCollapsed: boolean;
	chatListCollapsed: boolean;
	rightPanePinned: boolean;
}

function isValidSizes(value: unknown, length: number): value is number[] {
	if (!Array.isArray(value) || value.length !== length) return false;
	if (!value.every((size) => typeof size === "number" && Number.isFinite(size) && size > 0)) {
		return false;
	}
	let total = 0;
	for (const size of value) total += size;
	return Math.abs(total - 100) < 1;
}

function validBottomPaneHeight(value: unknown): value is number {
	return typeof value === "number" && Number.isFinite(value) && value >= 15 && value <= 60;
}

function validGroupWidth(value: unknown): value is number {
	return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * 保存時と表示中でグループ幅が違うとき、左ペインの実寸を保つ比率 (%) を返す。
 * 比率のまま復元すると、外部ディスプレイで合わせた幅がノート PC では別の実寸になり、
 * 行き来のたびに調整し直すことになる。復元不要 (同じ幅・記録なし) なら null。
 */
export function rescaleLeftPaneSize(
	savedPercent: number,
	savedGroupWidth: number | null,
	groupWidth: number,
	minPercent: number,
): number | null {
	if (savedGroupWidth === null || savedGroupWidth <= 0 || groupWidth <= 0) return null;
	if (savedGroupWidth === groupWidth) return null;
	const percent = (savedPercent * savedGroupWidth) / groupWidth;
	return Math.min(Math.max(percent, minPercent), 100 - WORKSPACE_MIN_SIZE);
}

/** 永続化レイアウトの現行バージョン。上げるたびに migratePersistedLayoutState へ移行を足す。 */
export const LAYOUT_STATE_VERSION = 3;

/**
 * localStorage に保存されたレイアウトを現行バージョンへ移行する。
 * v1: 旧3カラム構成 (sizes) を、入れ子のワークスペース構成へ移行する。
 * v2: 下部ペイン化する前の高さ (terminalHeight) を引き継ぐ。
 * v3: LeftPane の上下を入れ替えた (上=環境ファイル / 下=チャット) ため、比率も入れ替える。
 */
export function migratePersistedLayoutState(
	value: unknown,
	version: number = LAYOUT_STATE_VERSION,
): PersistedLayoutState {
	const state = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
	const legacySizes = isValidSizes(state.sizes, 3) ? state.sizes : null;
	const workspaceSizes = isValidSizes(state.workspaceSizes, 2)
		? state.workspaceSizes
		: legacySizes
			? [legacySizes[0], legacySizes[1] + legacySizes[2]]
			: [...DEFAULT_WORKSPACE_SIZES];
	const legacyWorkspaceWidth = legacySizes ? legacySizes[1] + legacySizes[2] : 0;
	const centerRightSizes = isValidSizes(state.centerRightSizes, 2)
		? state.centerRightSizes
		: legacySizes && legacyWorkspaceWidth > 0
			? [
					(legacySizes[1] / legacyWorkspaceWidth) * 100,
					(legacySizes[2] / legacyWorkspaceWidth) * 100,
				]
			: [...DEFAULT_CENTER_RIGHT_SIZES];

	const storedLeftPaneSizes = isValidSizes(state.leftPaneSizes, 2) ? state.leftPaneSizes : null;

	return {
		workspaceSizes,
		workspaceGroupWidth: validGroupWidth(state.workspaceGroupWidth)
			? state.workspaceGroupWidth
			: null,
		centerRightSizes,
		leftPaneSizes: storedLeftPaneSizes
			? version < 3
				? [storedLeftPaneSizes[1], storedLeftPaneSizes[0]]
				: storedLeftPaneSizes
			: [...DEFAULT_LEFT_PANE_SIZES],
		bottomPaneHeight: validBottomPaneHeight(state.bottomPaneHeight)
			? state.bottomPaneHeight
			: validBottomPaneHeight(state.terminalHeight)
				? state.terminalHeight
				: DEFAULT_BOTTOM_PANE_HEIGHT,
		sidebarCollapsed: typeof state.sidebarCollapsed === "boolean" ? state.sidebarCollapsed : true,
		leftPaneCollapsed:
			typeof state.leftPaneCollapsed === "boolean" ? state.leftPaneCollapsed : false,
		environmentFilesCollapsed:
			typeof state.environmentFilesCollapsed === "boolean"
				? state.environmentFilesCollapsed
				: false,
		chatListCollapsed:
			typeof state.chatListCollapsed === "boolean" ? state.chatListCollapsed : false,
		rightPanePinned: typeof state.rightPanePinned === "boolean" ? state.rightPanePinned : true,
	};
}
